import React, { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  messageBoxShowAction,
  messageBoxHideAction,
} from 'src/reducers/message-box/action'
import { ButtonActionType, ButtonData, ButtonType } from 'src/model/button'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'

const WARNING_TITLE = `<div style="display: flex; align-items: center; justify-content: center; gap: 8px; color: var(--mcg-dark, #002E47); font-family: Inter; font-size: 18px; font-weight: 600; line-height: 28px;">
  <Typography>Leave page?</Typography>
</div>`
const DEFAULT_WARNING_DESCRIPTION =
  'If you leave now, your AI outline will be lost and you must start over.'

const useOutlineLeaveGuard = (
  enabled,
  warningDescription = DEFAULT_WARNING_DESCRIPTION,
) => {
  const history = useHistory()
  const dispatch = useDispatch()

  const unblockRef = useRef(null)
  const isBypassingRef = useRef(false)
  const isPromptOpenRef = useRef(false)
  const pendingTransitionRef = useRef(null)

  useEffect(() => {
    if (!enabled) {
      isPromptOpenRef.current = false
      pendingTransitionRef.current = null
      isBypassingRef.current = false
      if (unblockRef.current) {
        unblockRef.current()
        unblockRef.current = null
      }
      return
    }

    const handleCancel = () => {
      isPromptOpenRef.current = false
      pendingTransitionRef.current = null
      dispatch(messageBoxHideAction())
    }

    const continueNavigation = () => {
      const pendingTransition = pendingTransitionRef.current
      if (!pendingTransition) {
        isPromptOpenRef.current = false
        dispatch(messageBoxHideAction())
        return
      }

      isBypassingRef.current = true
      isPromptOpenRef.current = false
      pendingTransitionRef.current = null
      dispatch(messageBoxHideAction())

      if (unblockRef.current) {
        unblockRef.current()
        unblockRef.current = null
      }

      const { location, action } = pendingTransition
      if (action === 'REPLACE') {
        history.replace(location)
      } else if (action === 'POP') {
        history.goBack()
      } else {
        history.push(location)
      }
    }

    const showLeaveWarning = () => {
      const warningDescriptionNode = React.createElement(
        'span',
        { style: { whiteSpace: 'pre-line' } },
        warningDescription,
      )

      dispatch(
        messageBoxShowAction(
          MessageBoxContent({
            type: MessageBoxType.WARNING,
            title: WARNING_TITLE,
            description: warningDescriptionNode,
            maxWidth: '500px',
            callback: () => {
              // Treat header-close like cancel so subsequent nav attempts can reopen the modal
              isPromptOpenRef.current = false
              pendingTransitionRef.current = null
            },
            buttons: [
              new ButtonData(
                ButtonType.NORMAL,
                'Cancel',
                ButtonActionType.NO,
                handleCancel,
              ),
              new ButtonData(
                ButtonType.ACTION,
                'Leave',
                ButtonActionType.YES,
                continueNavigation,
              ),
            ],
          }),
        ),
      )
    }

    unblockRef.current = history.block((location, action) => {
      if (isBypassingRef.current) {
        isBypassingRef.current = false
        return undefined
      }

      pendingTransitionRef.current = { location, action }

      if (!isPromptOpenRef.current) {
        isPromptOpenRef.current = true
        showLeaveWarning()
      }

      return false
    })

    const handleBeforeUnload = event => {
      if (!enabled) {
        return undefined
      }

      event.preventDefault()
      event.returnValue = warningDescription
      return warningDescription
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (unblockRef.current) {
        unblockRef.current()
        unblockRef.current = null
      }
      isPromptOpenRef.current = false
      pendingTransitionRef.current = null
      isBypassingRef.current = false
    }
  }, [dispatch, enabled, history, warningDescription])
}

export default useOutlineLeaveGuard
